<script setup lang="ts">
import type { OrganizationLogoSize } from './types'

const NuxtLinkLocale = resolveComponent('NuxtLinkLocale')

// Matches each size's largest CSS breakpoint so IPX generates the right
// dimensions instead of upscaling a smaller source.
const DIMENSIONS: Record<OrganizationLogoSize, number> = {
  xs: 24,
  sm: 32,
  md: 56,
  lg: 48
}

const SIZE_CLASS: Record<OrganizationLogoSize, string> = {
  xs: '!h-6 !w-6 !min-w-6',
  sm: '!h-8 !w-8 !min-w-8',
  md: '',
  lg: 'h-10 w-10 md:h-12 md:w-12'
}

// The wrapper's default padding is tuned for the larger sizes and crowds xs.
const WRAPPER_CLASS: Record<OrganizationLogoSize, string> = {
  xs: 'p-1',
  sm: '',
  md: '',
  lg: ''
}

const props = withDefaults(
  defineProps<{
    src?: string | null
    alt: string
    /** Wraps the logo in a link when provided. */
    to?: string
    /** Link's aria-label; falls back to `alt` when the caller has no distinct phrasing. */
    ariaLabel?: string
    highlighted?: boolean
    size?: OrganizationLogoSize
  }>(),
  {
    src: undefined,
    to: undefined,
    ariaLabel: undefined,
    highlighted: false,
    size: 'md'
  }
)
</script>

<template>
  <component
    :is="props.to ? NuxtLinkLocale : 'div'"
    :to="props.to"
    :aria-label="props.to ? (props.ariaLabel ?? props.alt) : undefined"
  >
    <div class="OrgLogoWrapper" :class="WRAPPER_CLASS[props.size]">
      <NuxtImg
        v-if="props.src"
        :src="props.src"
        :alt="props.alt"
        class="OrganizationLogo"
        :class="SIZE_CLASS[props.size]"
        :width="DIMENSIONS[props.size]"
        :height="DIMENSIONS[props.size]"
        fit="contain"
        background="white"
        placeholder
      />
      <div v-else class="OrganizationLogo" :class="SIZE_CLASS[props.size]" />
      <NuxtImg
        v-if="props.highlighted"
        src="/images/photostock/brilliant.png"
        width="24"
        height="24"
        :alt="$t('common.premium_marker_alt')"
        class="absolute bottom-[-6px] right-[-6px]"
        format="avif,webp"
      />
    </div>
  </component>
</template>
